import { Button } from '@hologram-dimension/button';
import { Chip } from '@hologram-dimension/chip';
import { Icon } from '@hologram-dimension/icon';
import { Link } from '@hologram-dimension/link';
import { Panel } from '@hologram-dimension/panel';
import { PanelNotification } from '@hologram-dimension/panel-notification';
import useBillingAccount from 'api/billing/useBillingAccount';
import useDownloadInvoiceCall from 'api/billing/useDownloadInvoiceCall';
import useInvoices from 'api/billing/useInvoices';
import clsx from 'clsx/lite';
import Field from 'components/Field/Field';
import Table from 'components/Table/Table';
import type { HeaderItem, TableCellItem, TableRowItem } from 'components/Table/types';
import { DateTime } from 'luxon';
import { useCallback, useMemo, useState, type FC, type ReactNode } from 'react';
import useOrganization from 'state/contexts/Organization/useOrganization';
import downloadPDF from 'utils/files/downloadPDF';
import formatMoney from 'utils/numbers/formatMoney';
import styles from './InvoicesPanel.module.css';

const PAGE_SIZE = 12 as const;

interface InvoicesPanelProps {
  legacyInvoices?: ReactNode;
}

const InvoicesPanel: FC<InvoicesPanelProps> = ({ legacyInvoices }) => {
  const organizationId = useOrganization();
  const {
    invoices,
    isLoading: isLoadingInvoices,
    isValidating,
    error: invoicesError,
    loadMore,
    end,
    isLoadingMore,
    isRefreshing,
    isEmpty,
  } = useInvoices({
    organizationId,
    pageSize: PAGE_SIZE,
  });

  const {
    billingAccount: { paymentTerm },
    isLoading: isLoadingBillingAccount,
    error: billingAccountError,
  } = useBillingAccount(organizationId);

  const isLoading = (isLoadingInvoices && !isEmpty) || isLoadingBillingAccount;
  const error = invoicesError || billingAccountError;

  const downloadInvoice = useDownloadInvoiceCall({ orgId: organizationId });

  const [downloadingInvoices, setDownloadingInvoices] = useState(new Set<InvoiceId>());

  const handleDownloadInvoice = useCallback(
    async (invoiceId?: string) => {
      if (!invoiceId) {
        return;
      }
      setDownloadingInvoices((prev) => new Set([...prev, invoiceId]));
      try {
        const pdf = await downloadInvoice(invoiceId);

        if (pdf) {
          downloadPDF({ blob: pdf, filename: `${invoiceId}.pdf` });
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Error downloading invoice:', err);
      } finally {
        setDownloadingInvoices((prev) => {
          const newSet = new Set(prev);
          newSet.delete(invoiceId);
          return newSet;
        });
      }
    },
    [downloadInvoice]
  );

  const panelContents = useMemo(() => {
    if (error) {
      return (
        <PanelNotification
          variant="error"
          label="We ran into an issue loading your SIM profile history"
          actions={
            <Button
              variant="secondary"
              iconStart="Sync"
              onClick={() => {
                window.location.reload();
              }}
            >
              Reload page
            </Button>
          }
        >
          We will keep trying in the background or you can try to reload the page.
        </PanelNotification>
      );
    }

    if (isLoading) {
      return <PanelNotification>Loading...</PanelNotification>;
    }

    if (isEmpty) {
      return (
        <PanelNotification icon="Receipt" label="No invoices available">
          {' '}
        </PanelNotification>
      );
    }

    const headers: Array<HeaderItem> = [
      { children: 'Invoice', key: 'invoice' },
      { children: 'Total charges', key: 'total-charges' },
      { children: 'Status', key: 'status' },
      { children: 'Due date', key: 'due-date' },
      { children: 'Actions', key: 'actions', className: styles.actions },
    ];

    let totalDue = 0;
    let unpaidInvoices = 0;

    const rows: Array<TableRowItem | null> = invoices.map((invoice) => {
      if (!invoice || !invoice.id || !invoice.invoiceDate) {
        return null; // Skip invalid invoices
      }

      let paid = true;
      if (invoice.totalCharges && invoice.status !== 'PAID' && (invoice.totalCharges ?? 0) > 0) {
        totalDue += invoice.totalCharges;
        unpaidInvoices += 1;
        paid = false;
      }

      const cells: Array<TableCellItem> = new Array(5);
      const rowKey = `${invoice.id}`;

      cells[0] = {
        key: `${rowKey}-invoice`,
        className: styles.invoice,
        children: (
          <Link onClick={() => handleDownloadInvoice(invoice.id)}>
            {DateTime.fromFormat(invoice.invoiceDate, 'yyyy-MM-dd').toFormat('MMMM yyyy')}
          </Link>
        ),
      };

      cells[1] = {
        key: `${rowKey}-total-charges`,
        className: clsx(styles.totalCharges, !paid && styles.totalChargesUnpaid),
        children: formatMoney(invoice.totalCharges),
      };

      let statusChip;
      switch (invoice.status) {
        case 'PAID':
          statusChip = (
            <Chip
              type="informational"
              size="medium"
              className={clsx(styles.statusChip, styles.statusChipPaid)}
              iconStart={
                <Icon name="CircleCheckmark" fill="DdsColorFillNotificationSuccess" size="small" />
              }
            >
              Paid
            </Chip>
          );
          break;

        case 'PAST_DUE':
          statusChip = (
            <Chip
              type="informational"
              size="medium"
              className={clsx(styles.statusChip, styles.statusChipPastDue)}
              iconStart={
                <Icon name="CircleAlert" fill="DdsColorFillNotificationError" size="small" />
              }
            >
              Past due
            </Chip>
          );
          break;

        case 'UNPAID':
          statusChip = (
            <Chip
              type="informational"
              size="medium"
              className={clsx(styles.statusChip, styles.statusChipUnpaid)}
              iconStart={<Icon name="Warning" fill="DdsColorPaletteOrange60" size="small" />}
            >
              Unpaid
            </Chip>
          );
          break;

        default:
          statusChip = undefined;
          break;
      }
      cells[2] = {
        key: `${rowKey}-status`,
        children: statusChip,
      };

      cells[3] = {
        key: `${rowKey}-due-date`,
        children: invoice.paidDate ? (
          <span className={styles.duePaid}>Paid {invoice.paidDate}</span>
        ) : (
          <span className={invoice.status === 'PAST_DUE' ? styles.duePastDue : styles.due}>
            {invoice.dueDate}
          </span>
        ),
      };
      cells[4] = {
        key: `${rowKey}-actions`,
        className: styles.actions,
        children: (
          <div className={styles.actionsWrapper}>
            {!paid && invoice.paymentLink && (
              <Button
                key="pay-invoice"
                variant="primary"
                iconEnd="ExternalLink"
                href={invoice.paymentLink}
                target="_blank"
                rel="noreferrer"
              >
                Pay invoice
              </Button>
            )}
            <Button
              variant="secondary"
              onClick={() => handleDownloadInvoice(invoice.id)}
              key="download-invoice"
              loading={downloadingInvoices.has(invoice.id)}
            >
              Download PDF
            </Button>
          </div>
        ),
      };

      return { key: rowKey, cells };
    });

    return (
      <div className={styles.contents}>
        <div className={styles.header}>
          <Field
            label={`Total due from ${unpaidInvoices} invoices`}
            labelClassName={styles.label}
            value={formatMoney(totalDue)}
            valueClassname={styles.value}
            direction="column"
          />
          <Field
            label="Payment terms"
            labelClassName={styles.label}
            value={paymentTerm}
            valueClassname={styles.value}
            direction="column"
          />
        </div>
        <Table
          headers={headers}
          rows={rows.filter((row) => !!row)}
          wrapperClassName={styles.table}
        />
      </div>
    );
  }, [
    downloadingInvoices,
    error,
    handleDownloadInvoice,
    invoices,
    isEmpty,
    isLoading,
    paymentTerm,
  ]);

  return (
    <>
      <Panel
        header="Invoices"
        noContentPadding
        isLoading={isLoading || isValidating || isLoadingMore || isRefreshing}
        className={styles.panel}
        footerActions={
          !end ? (
            <Button onClick={loadMore} variant="secondary" loading={isLoadingMore}>
              Load older invoices
            </Button>
          ) : undefined
        }
      >
        {panelContents}
      </Panel>
      {(end || isEmpty) && legacyInvoices}
    </>
  );
};

export default InvoicesPanel;
